import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Save } from 'lucide-react';
import { Section, Field, Modal } from './_shared';

const empty = { n: '', arabic: '', title: '', text: '', order: 0 };

export default function PillarsPanel({ cx }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => { setLoading(true); try { const r = await cx.get('/pillars'); setItems(r.data || []); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...editing }; delete payload.id; delete payload.created_at;
      if (editing.id) await cx.put(`/pillars/${editing.id}`, payload);
      else await cx.post('/pillars', payload);
      toast.success('Saved'); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this pillar?')) return;
    try { await cx.delete(`/pillars/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Delete failed'); }
  };

  return (
    <Section title="QNDS3 Pillars" action={<button data-testid="admin-pillar-add" onClick={() => setEditing({ ...empty })} className="btn-primary text-sm"><Plus size={14} /> Add pillar</button>}>
      {loading ? <p className="text-sec text-sm font-mono">Loading…</p> : items.length === 0 ? <p className="text-muted text-sm">No pillars yet.</p> : (
        <div className="space-y-2">
          {items.map((p) => (
            <div key={p.id} data-testid={`admin-pillar-row-${p.id}`} className="border border-subtle surface rounded-md p-4 flex gap-4 items-start">
              <div className="w-12 h-12 rounded-md surface-2 flex items-center justify-center font-mono text-brand-teal-light shrink-0">{p.n}</div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-prim">{p.title}</p>
                {p.text && <p className="text-sec text-sm mt-1 line-clamp-2">{p.text}</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEditing(p)} className="text-xs text-brand-teal-light hover:underline flex items-center gap-1"><Pencil size={12} /> Edit</button>
                <button onClick={() => del(p.id)} className="text-xs text-brand-maroon-light hover:underline flex items-center gap-1"><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? 'Edit pillar' : 'New pillar'}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Number (e.g. 01)"><input className="lux-input font-mono" value={editing.n} onChange={(e) => setEditing({ ...editing, n: e.target.value })} /></Field>
              <Field label="Order"><input type="number" className="lux-input" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || 0) })} /></Field>
            </div>
            <Field label="Arabic accent"><input className="lux-input font-arabic" value={editing.arabic} onChange={(e) => setEditing({ ...editing, arabic: e.target.value })} /></Field>
            <Field label="Title"><input className="lux-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></Field>
            <Field label="Description"><textarea rows={4} className="lux-input resize-none" value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} /></Field>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setEditing(null)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={save} data-testid="admin-pillar-save" className="btn-primary text-sm"><Save size={14} /> Save</button>
            </div>
          </div>
        </Modal>
      )}
    </Section>
  );
}
